import DOMPurify from 'dompurify';

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDownIcon } from '../components/icons/ChevronDownIcon';
import { useContent } from '../hooks/useContent';
import { useSeo } from '../hooks/useSeo';
import { AdminControls } from '../components/AdminControls';
import type { PageContent } from '../types';
import { useToast } from '../hooks/useToast';

export const HelpPage: React.FC = () => {
  const navigate = useNavigate();
  const { content: allContent, isAdminMode, updatePageContent } = useContent();
  const [pageContent, setPageContent] = useState<PageContent>(allContent.help);
  const { showToast } = useToast();
  
  const onSeoApplied = useCallback(() => {
    window.dispatchEvent(new Event('app-rendered'));
  }, []);

  useSeo(pageContent, 'help', onSeoApplied);

  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useEffect(() => {
    setPageContent(allContent.help);
  }, [allContent.help]);

  const handleSave = async () => {
    const oldSlug = allContent.help?.seo?.slug;
    const redirectCreated = await updatePageContent('help', pageContent);
    showToast('Help page content saved!');
    if (redirectCreated) {
      const newSlug = pageContent.seo.slug;
      showToast(`Redirect created: ${oldSlug} -> ${newSlug}`);
    }
  };

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="w-full max-w-6xl mx-auto py-8 md:py-12 px-4 sm:px-6 lg:px-8 text-[#1F2937]">
      <div className="w-full max-w-3xl mx-auto">
        <header className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-[#4F46E5]">
            {pageContent.h1}
          </h1>
          <p className="text-[#6B7280] mt-3 text-lg">
            {pageContent.body}
          </p>
        </header>

        <div className="bg-white shadow-lg rounded-xl p-6 md:p-8 border border-gray-200">
            <div className="space-y-4">
                {(pageContent.faqs || []).map((faq, index) => {
                    const sanitizedAnswer = faq.answer ? DOMPurify.sanitize(faq.answer, { ADD_ATTR: ['class'] }) : '';
                    return (
                    <div key={index} className="border-b border-gray-200 pb-4 last:border-b-0">
                        <button
                            onClick={() => toggleFAQ(index)}
                            className="w-full flex justify-between items-center text-left py-2 focus:outline-none"
                            aria-expanded={openIndex === index}
                            aria-controls={`faq-answer-${index}`}
                        >
                            <span className="text-lg font-semibold text-[#1F2937]">
                                {faq.question}
                            </span>
                            <ChevronDownIcon className={`w-6 h-6 text-gray-500 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} />
                        </button>
                        <div
                            id={`faq-answer-${index}`}
                            className={`overflow-hidden transition-all duration-300 ease-in-out ${openIndex === index ? 'max-h-[500px]' : 'max-h-0'}`}
                        >
                            <div 
                                className="pt-3 text-[#6B7280] leading-relaxed prose"
                                dangerouslySetInnerHTML={{ __html: sanitizedAnswer }}
                            />
                        </div>
                    </div>
                )})}
            </div>
        
            {isAdminMode && (
              <AdminControls 
                  pageId="help" 
                  pageContent={pageContent} 
                  onContentChange={setPageContent}
                  onSave={handleSave}
              />
            )}

            <div className="text-center pt-10">
                <button
                onClick={() => navigate('/')}
                className="inline-block py-3 px-8 bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold rounded-lg shadow-md transition-all duration-300"
                >
                Go to Brand Name Generator
                </button>
            </div>
        </div>
      </div>
    </div>
  );
};
